<template>
  <div class="watch-graph">
    <div v-if="hasChecked" ref="chartEl" class="watch-graph__canvas"></div>
    <p v-else class="watch-graph__empty text-muted">
      Check vitals in the grid above to add them to the graph.
    </p>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapGetters } from 'vuex';
import * as echarts from 'echarts';
import { getDescByCcd } from '../../../../data/getters';

const COLORS = ['#3498db', '#e74c3c', '#e67e22', '#27ae60', '#9b59b6', '#f39c12', '#1abc9c'];

const getMeasIdsForCcd = (measCcdMeasIds, ccd) => (
  measCcdMeasIds?.[ccd]
  ?? measCcdMeasIds?.[String(ccd)]
  ?? measCcdMeasIds?.[Number(ccd)]
  ?? []
);

const parseMeasureTime = (measure) => {
  const raw = measure?.lotimestamp || measure?.lodate;
  if (!raw) return null;
  const normalized = String(raw).trim().replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const formatAxisDate = (timestamp) => {
  const date = new Date(timestamp);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}/${day} ${hours}:${minutes}`;
};

export default {
  name: 'watch-graph',
  props: {
    type: {
      type: String,
      default: 'watch',
    },
    selected: {
      default: null,
    },
    range: {
      default: null,
    },
  },
  data() {
    return {
      _resizeTimeout: null,
    };
  },
  created() {
    // Keep ECharts off Vue's reactive system — proxying breaks coordinateSystem.
    this._chart = null;
    this._renderRaf = 0;
  },
  computed: {
    ...mapGetters('login/watch', {
      checkedMeasuresOnly: 'getCheckedMeasuresOnly',
      checkedVitalsOnly: 'getCheckedVitalsOnly',
      areExternalMeasuresLoaded: 'areExternalMeasuresLoaded',
    }),
    ...mapGetters('login', {
      allData: 'getAllData',
    }),
    checkedCcds() {
      const checked = (this.type === 'vitals' || this.type === 'all')
        ? this.checkedVitalsOnly
        : this.checkedMeasuresOnly;
      return Object.keys(checked || {});
    },
    hasChecked() {
      return this.checkedCcds.length > 0;
    },
    chartOption() {
      if (!this.allData?.measures || !this.hasChecked) {
        return null;
      }

      const cutoff = this.getRangeCutoff();
      const pointsByCcd = {};
      const allTimes = new Set();

      this.checkedCcds.forEach((ccd) => {
        const points = this.getLineData(ccd, cutoff);
        if (!points.length) return;
        pointsByCcd[ccd] = points;
        points.forEach(([time]) => allTimes.add(time));
      });

      if (!allTimes.size) {
        return null;
      }

      const sortedTimes = [...allTimes].sort((a, b) => a - b);
      const axisLabels = sortedTimes.map(formatAxisDate);
      const series = [];
      const legend = [];
      const allValues = [];

      this.checkedCcds.forEach((ccd) => {
        const points = pointsByCcd[ccd];
        if (!points) return;

        const valuesByTime = {};
        points.forEach(([time, value]) => {
          valuesByTime[time] = value;
        });

        const data = sortedTimes.map((time) => {
          const value = valuesByTime[time];
          if (value == null) return null;
          allValues.push(value);
          return value;
        });

        const name = getDescByCcd(ccd, this.allData)
          || this.allData.ccds?.[ccd]?.desc
          || String(ccd);
        legend.push(name);

        series.push({
          name,
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          sampling: false,
          connectNulls: true,
          showSymbol: true,
          symbolSize: 7,
          data,
          lineStyle: { width: 2, color: COLORS[series.length % COLORS.length] },
          itemStyle: { color: COLORS[series.length % COLORS.length] },
        });
      });

      if (!series.length) {
        return null;
      }

      const yAxis = this.getAutoYAxis(allValues);

      return {
        color: COLORS,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
          },
        },
        legend: {
          data: legend,
        },
        grid: {
          left: '8%',
          right: '4%',
          top: '14%',
          bottom: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: axisLabels,
          axisLabel: {
            rotate: axisLabels.length > 7 ? 45 : 0,
          },
        },
        yAxis: {
          type: 'value',
          min: yAxis.min,
          max: yAxis.max,
          scale: true,
        },
        series,
      };
    },
  },
  watch: {
    checkedCcds() {
      this.scheduleRender();
    },
    range() {
      this.scheduleRender();
    },
    areExternalMeasuresLoaded(loaded) {
      if (loaded) {
        this.scheduleRender();
      }
    },
    hasChecked(newVal) {
      if (!newVal && this._chart) {
        this._chart.dispose();
        this._chart = null;
        return;
      }
      this.scheduleRender();
    },
  },
  mounted() {
    this._onResize = () => {
      if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        if (this._chart) this._chart.resize();
      }, 200);
    };
    window.addEventListener('resize', this._onResize, { passive: true });
    this.scheduleRender();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    if (this._renderRaf) cancelAnimationFrame(this._renderRaf);
    if (this._chart) {
      this._chart.dispose();
      this._chart = null;
    }
  },
  methods: {
    scheduleRender() {
      if (this._renderRaf) {
        cancelAnimationFrame(this._renderRaf);
      }
      this._renderRaf = requestAnimationFrame(() => {
        this._renderRaf = 0;
        this.$nextTick(() => this.renderChart());
      });
    },
    getRangeCutoff() {
      if (!this.range) return null;
      const days = Number(this.range);
      if (!Number.isFinite(days) || days <= 0) return null;
      return Date.now() - ((days - 1) * 86400000);
    },
    getLineData(ccd, cutoff) {
      const measIds = getMeasIdsForCcd(this.allData?.measCcdMeasIds, ccd);
      const points = [];

      measIds.forEach((measId) => {
        const measure = this.allData.measures[measId];
        if (!measure) return;

        const time = parseMeasureTime(measure);
        const value = Number(measure.value);
        if (time == null || !Number.isFinite(value)) return;
        if (cutoff != null && time < cutoff) return;

        points.push([time, value]);
      });

      points.sort((a, b) => a[0] - b[0]);
      return points;
    },
    getAutoYAxis(values) {
      const nums = (values || []).filter((v) => Number.isFinite(v));
      if (!nums.length) {
        return { min: 0, max: 100 };
      }

      const minV = Math.min(...nums);
      const maxV = Math.max(...nums);
      if (minV === maxV) {
        const pad = Math.max(1, Math.ceil(Math.abs(maxV) * 0.1));
        return { min: Math.max(0, minV - pad), max: maxV + pad };
      }

      const range = maxV - minV;
      const pad = Math.max(1, Math.ceil(range * 0.15));
      return {
        min: Math.max(0, minV - pad),
        max: maxV + pad,
      };
    },
    renderChart() {
      if (!this.areExternalMeasuresLoaded || !this.hasChecked) {
        if (this._chart) {
          this._chart.dispose();
          this._chart = null;
        }
        return;
      }

      const el = this.$refs.chartEl;
      if (!el || el.offsetWidth === 0) return;

      const option = this.chartOption;
      if (!option) {
        if (this._chart) {
          this._chart.clear();
        }
        return;
      }

      if (!this._chart) {
        this._chart = markRaw(echarts.init(el));
      }

      try {
        this._chart.setOption(option, { notMerge: true });
        this._chart.resize();
      } catch (error) {
        console.error('Error rendering vitals graph:', error);
        if (this._chart) {
          this._chart.dispose();
          this._chart = null;
        }
      }
    },
  },
};
</script>

<style scoped>
.watch-graph {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.watch-graph__canvas {
  width: 100%;
  height: 340px;
  background: #f8f9fa;
  border-radius: 8px;
}

.watch-graph__empty {
  margin: 0;
  padding: 1.5rem;
  text-align: center;
  background: #f8f9fa;
  border: 1px dashed #d6dde5;
  border-radius: 8px;
}
</style>
